import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';

/**
 * SlashCommandMenu — searchable quick-insert menu opened by typing "/".
 * Lists only the starter block set (more types register here later).
 */

export const BLOCK_COMMANDS = [
  { id: 'text', label: 'Text', description: 'Plain paragraph', icon: '¶', keywords: ['text', 'paragraph', 'p'] },
  { id: 'heading-1', label: 'Heading 1', description: 'Large heading', icon: 'H1', keywords: ['h1', 'heading', 'title', 'large'] },
  { id: 'heading-2', label: 'Heading 2', description: 'Medium heading', icon: 'H2', keywords: ['h2', 'heading', 'subtitle'] },
  { id: 'heading-3', label: 'Heading 3', description: 'Small heading', icon: 'H3', keywords: ['h3', 'heading', 'section'] },
  { id: 'heading-4', label: 'Heading 4', description: 'Smaller heading', icon: 'H4', keywords: ['h4', 'heading', 'subsection'] },
  { id: 'checkbox', label: 'Checkbox', description: 'Step you can tick off', icon: '☑', keywords: ['checkbox', 'todo', 'check', 'task', 'step', 'done', '[]'] },
  { id: 'file-upload', label: 'File upload', description: 'Per-mission file — each run fills this slot', icon: '📎', keywords: ['upload', 'file', 'attachment', 'deliverable', 'pdf', 'field'] },
  { id: 'bullet', label: 'Bulleted list', description: 'Simple bullet point', icon: '•', keywords: ['bullet', 'list', 'ul', 'point', '-'] },
  { id: 'number', label: 'Numbered list', description: 'Numbered list item', icon: '1.', keywords: ['number', 'numbered', 'list', 'ol', 'ordered', '1.'] },
  { id: 'choice', label: 'Choice', description: 'Section with tabs — pick a path, or show all', icon: '◇', keywords: ['choice', 'choose', 'if', 'branch', 'path', 'tab', 'option', 'switch', 'conditional'] },
  { id: 'callout', label: 'Section', description: 'Boxed section you can color, nest, and split into columns', icon: '💡', keywords: ['section', 'callout', 'div', 'container', 'group', 'info', 'box', 'highlight', 'note', 'tip', 'warning'] },
  { id: 'quote', label: 'Quote', description: 'Pull quote or citation', icon: '❝', keywords: ['quote', 'blockquote', 'citation', '>', 'quotation'] },
  { id: 'toggle', label: 'Toggle', description: 'Collapsible section that hides content', icon: '▸', keywords: ['toggle', 'dropdown', 'collapse', 'expand', 'fold', 'accordion', 'details', 'envelope'] },
  { id: 'code', label: 'Code', description: 'Code block with syntax label', icon: '</>', keywords: ['code', 'pre', 'monospace', 'terminal', 'script', '```'] },
  { id: 'divider', label: 'Divider', description: 'Horizontal separator line', icon: '—', keywords: ['divider', 'line', 'hr', 'separator', 'rule', '---'] },
  { id: 'image', label: 'Image', description: 'Upload or paste a picture', icon: '▣', keywords: ['image', 'img', 'picture', 'photo', 'screenshot', 'upload'] },
  { id: 'video', label: 'Video', description: 'Embed a link or upload MP4/WebM (100MB)', icon: '▶', keywords: ['video', 'embed', 'mp4', 'webm', 'movie', 'film', 'media', 'upload'] },
  { id: 'youtube', label: 'YouTube', description: 'Embed a YouTube video', icon: '▶', keywords: ['youtube', 'yt', 'video', 'embed'] },
  { id: 'vimeo', label: 'Vimeo', description: 'Embed a Vimeo video', icon: '▶', keywords: ['vimeo', 'video', 'embed'] },
  { id: 'loom', label: 'Loom', description: 'Embed a Loom video', icon: '▶', keywords: ['loom', 'video', 'embed', 'recording'] },
  { id: 'table', label: 'Table', description: 'Grid with header row', icon: '▦', keywords: ['table', 'grid', 'rows', 'columns', 'spreadsheet', 'matrix'] },
  { id: 'variables', label: 'Variables', description: 'Define values this doc can use', icon: '{x}', keywords: ['variable', 'variables', 'var', 'value', 'placeholder', '{{'] },
  { id: 'linked-variable-row', label: 'Linked variable row', description: 'One row from the variables table — edit it here, updates everywhere', icon: '{1}', keywords: ['linked', 'variable', 'row', 'field', 'portal', 'edit', 'value', 'bind'] },
  { id: 'row', label: '2 Columns', description: 'Side-by-side layout', icon: '⬌', keywords: ['row', 'columns', 'side by side', '2 columns', 'layout'] },
  { id: 'doc', label: 'Doc link', description: 'Mention a doc from the library', icon: '📄', keywords: ['doc', 'docs', 'sop', 'link', 'mention', 'library'] },
  { id: 'snippet', label: 'Snippet', description: 'Embed a block from another doc', icon: '⧉', keywords: ['snippet', 'embed', 'reference', 'transclude', 'cite', 'block'] },
  { id: 'variable', label: 'Variable pill', description: 'Insert a value defined by the doc or campaign', icon: '{}', keywords: ['variable', 'var', 'pill', 'insert', '{{'] },
  { id: 'math', label: 'Math', description: 'Calculate with + − × ÷ and {{number variables}}', icon: '∑', keywords: ['math', 'formula', 'calculate', 'calc', 'plus', 'minus', 'multiply', 'divide'] },
];

export const SlashCommandMenu = ({
  position,
  onSelect,
  onClose,
  searchQuery = '',
  hasVariablesBlock = false,
  excludeIds = null,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commands = useMemo(
    () =>
      BLOCK_COMMANDS.filter((cmd) => !excludeIds?.includes(cmd.id)).map((cmd) => {
        if (cmd.id === 'variables' && hasVariablesBlock) {
          return {
            ...cmd,
            disabled: true,
            description: 'Only one allowed per doc',
          };
        }
        return cmd;
      }),
    [hasVariablesBlock, excludeIds]
  );
  const [filteredCommands, setFilteredCommands] = useState(commands);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const menuRef = useRef(null);

  // Keep the menu inside the visible doc surface
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => {
      setAdjustedPosition(clampFloatingMenuPosition(menu, position));
    };
    apply();
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(menu);
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, true);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply, true);
    };
  }, [position, filteredCommands]);

  // Filter + sort by relevance as the user types after the "/"
  useEffect(() => {
    if (!searchQuery) {
      setFilteredCommands(commands);
      setSelectedIndex(0);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = commands.filter(
      (cmd) =>
        cmd.label.toLowerCase().includes(query) ||
        cmd.description.toLowerCase().includes(query) ||
        cmd.keywords.some((kw) => kw.includes(query))
    );
    filtered.sort((a, b) => {
      const aLabel = a.label.toLowerCase();
      const bLabel = b.label.toLowerCase();
      if (aLabel === query && bLabel !== query) return -1;
      if (bLabel === query && aLabel !== query) return 1;
      if (aLabel.startsWith(query) && !bLabel.startsWith(query)) return -1;
      if (bLabel.startsWith(query) && !aLabel.startsWith(query)) return 1;
      return 0;
    });
    setFilteredCommands(filtered);
    setSelectedIndex(0);
  }, [searchQuery, commands]);

  const pick = (cmd) => {
    if (!cmd || cmd.disabled) return;
    onSelect(cmd.id);
  };

  // Keyboard navigation (capture phase so the block underneath never sees it)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex((prev) => (prev + 1) % Math.max(filteredCommands.length, 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        setSelectedIndex(
          (prev) => (prev - 1 + filteredCommands.length) % Math.max(filteredCommands.length, 1)
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        e.stopPropagation();
        const cmd = filteredCommands[selectedIndex];
        if (!cmd || cmd.disabled) return;
        onSelect(cmd.id);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [selectedIndex, filteredCommands, onSelect, onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return createPortal(
    <div
      ref={menuRef}
      className="doc-slash-menu"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x }}
    >
      {filteredCommands.length === 0 ? (
        <div className="doc-slash-empty">No results found</div>
      ) : (
        filteredCommands.map((cmd, index) => (
          <div
            key={cmd.id}
            className={[
              'doc-slash-item',
              index === selectedIndex ? 'selected' : '',
              cmd.disabled ? 'doc-slash-item--disabled' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            aria-disabled={cmd.disabled || undefined}
            title={cmd.disabled ? cmd.description : undefined}
            onClick={(e) => {
              e.stopPropagation();
              pick(cmd);
            }}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <span className="doc-slash-icon">{cmd.icon}</span>
            <span className="doc-slash-label">{cmd.label}</span>
            <span className="doc-slash-description">· {cmd.description}</span>
          </div>
        ))
      )}
    </div>,
    document.body
  );
};
